import type { PainelNavKey } from '@/components/painel/painel-nav';
import type { CmsArticle, CmsCategory } from '@/lib/cms-types';
import type { AceCmsRecord } from '@/lib/aces-cms';
import type { PowerCmsRecord } from '@/lib/powers-cms';
import { canAccessNavKey, type PainelRole } from '@/lib/painel-rbac';

export type PainelSearchContentKind = 'guia' | 'power' | 'ace' | 'categoria' | 'pagina';

export type PainelSearchContentEntry = {
  id: string;
  kind: PainelSearchContentKind;
  label: string;
  description: string;
  href: string;
  /** Texto extra para o filtro do cmdk. */
  keywords: string;
};

const PAINEL_STATIC_PAGES: Array<{
  navKey: PainelNavKey;
  label: string;
  description: string;
  href: string;
  keywords?: string;
}> = [
  {
    navKey: 'homepage',
    label: 'Homepage — Hero',
    description: 'Cabeçalho e botões da página inicial',
    href: '/painel/paginas/homepage',
    keywords: 'inicio hero busca',
  },
  {
    navKey: 'paginas',
    label: 'Regras e código de conduta',
    description: 'Editor das páginas /regras e /conduta',
    href: '/painel/paginas/regras',
    keywords: 'regras conduta',
  },
  {
    navKey: 'regras',
    label: 'Regras',
    description: 'Conteúdo público da página /regras',
    href: '/painel/paginas/regras',
    keywords: 'regras rules biblioteca comunidade',
  },
  {
    navKey: 'conduta',
    label: 'Código de Conduta',
    description: 'Conteúdo público da página /conduta',
    href: '/painel/paginas/conduta',
    keywords: 'conduta codigo diretrizes equipe',
  },
  {
    navKey: 'paginas',
    label: 'Página da equipe — Textos',
    description: 'Título, subtítulo e descrições de /equipe',
    href: '/painel/paginas/equipe',
    keywords: 'staff equipe textos descricoes cargos',
  },
  {
    navKey: 'paginas',
    label: 'Hub de páginas',
    description: 'Visão geral das páginas institucionais',
    href: '/painel/paginas',
    keywords: 'paginas institucional',
  },
  {
    navKey: 'changelog_overview',
    label: 'Changelog — Overview',
    description: 'Resumo das versões do xat',
    href: '/painel/changelog/xat/overview',
    keywords: 'changelog versoes xat',
  },
  {
    navKey: 'changelog_versions',
    label: 'Changelog — Versões',
    description: 'Lista e edição de versões',
    href: '/painel/changelog/xat/versions',
    keywords: 'changelog releases',
  },
  {
    navKey: 'aces',
    label: 'Aces — Painel',
    description: 'Gestão de coleções de Aces',
    href: '/painel/aces',
    keywords: 'aces catalogo cms',
  },
  {
    navKey: 'powers',
    label: 'Powerpédia — Lista',
    description: 'Todos os powers do Help Center',
    href: '/painel/powers',
    keywords: 'powers lista catalogo',
  },
  {
    navKey: 'guias',
    label: 'Guias — Lista',
    description: 'Artigos e rascunhos por categoria',
    href: '/painel/guias',
    keywords: 'guias artigos lista',
  },
  {
    navKey: 'categorias',
    label: 'Categorias — Lista',
    description: 'Organização da base de conhecimento',
    href: '/painel/categorias',
    keywords: 'categorias tags',
  },
  {
    navKey: 'textos_painel',
    label: 'Textos do painel',
    description: 'Títulos e descrições internas das páginas do painel',
    href: '/painel/textos',
    keywords: 'textos titulos descricoes internos ui labels',
  },
  {
    navKey: 'identidade',
    label: 'Identidade do site',
    description: 'Título, subtítulo, favicon e rodapé do site público',
    href: '/painel/identidade',
    keywords: 'identidade footer rodape favicon titulo subtitulo marca copyright links changelog lojas',
  },
  {
    navKey: 'coming_soon',
    label: 'Coming Soon',
    description: 'Modo manutenção pública e textos da página em breve',
    href: '/painel/coming-soon',
    keywords: 'coming soon manutencao em breve landing',
  },
  {
    navKey: 'configuracoes',
    label: 'Configurações da conta',
    description: 'Perfil, segurança e preferências pessoais',
    href: '/painel/configuracoes',
    keywords: 'conta perfil senha preferencias avatar',
  },
];

function canShow(role: PainelRole, navKey: PainelNavKey): boolean {
  return canAccessNavKey(role, navKey);
}

export function buildPainelSearchContentEntries(input: {
  role: PainelRole;
  articles: CmsArticle[];
  categories: CmsCategory[];
  powers: PowerCmsRecord[];
  aces?: AceCmsRecord[];
  getCategoryName?: (id: string) => string | undefined;
}): PainelSearchContentEntry[] {
  const { role, articles, categories, powers, aces = [], getCategoryName } = input;
  const out: PainelSearchContentEntry[] = [];

  if (canShow(role, 'guias')) {
    for (const article of articles) {
      const categoryName = getCategoryName?.(article.categoryId) ?? article.categoryId;
      const statusLabel = article.status === 'published' ? 'publicado' : 'rascunho';
      out.push({
        id: `guia-${article.id}`,
        kind: 'guia',
        label: article.title,
        description: `Guia · ${categoryName} · ${statusLabel}`,
        href: `/painel/guias/${article.id}/edit`,
        keywords: `guia artigo ${article.title} ${categoryName} ${article.slug ?? ''} ${statusLabel}`,
      });
    }
  }

  if (canShow(role, 'powers')) {
    for (const power of powers) {
      out.push({
        id: `power-${power.slug}`,
        kind: 'power',
        label: power.name,
        description: `Power · ${power.slug}${power.limited ? ' · limitado' : ''}`,
        href: `/painel/powers/${encodeURIComponent(power.slug)}/edit`,
        keywords: `power powerpedia ${power.name} ${power.slug} ${power.description?.short ?? ''} ${power.description?.long ?? ''} ${(power.smilies ?? []).join(' ')}`,
      });
    }
  }

  if (canShow(role, 'aces')) {
    for (const ace of aces) {
      out.push({
        id: `ace-${ace.slug}`,
        kind: 'ace',
        label: ace.name,
        description: `Ace · ${ace.shortDescription}`,
        href: `/aces/${ace.slug}`,
        keywords: `ace ${ace.name} ${ace.slug} ${ace.shortDescription}`,
      });
    }
  }

  if (canShow(role, 'categorias')) {
    for (const category of categories) {
      out.push({
        id: `cat-${category.id}`,
        kind: 'categoria',
        label: category.name,
        description: category.description || 'Categoria do Help Center',
        href: '/painel/categorias',
        keywords: `categoria ${category.name} ${category.description ?? ''}`,
      });
    }
  }

  for (const page of PAINEL_STATIC_PAGES) {
    if (!canShow(role, page.navKey)) continue;
    out.push({
      id: `page-${page.href}`,
      kind: 'pagina',
      label: page.label,
      description: page.description,
      href: page.href,
      keywords: `pagina ${page.label} ${page.description} ${page.keywords ?? ''}`,
    });
  }

  return out;
}

export const PAINEL_SEARCH_KIND_LABELS: Record<PainelSearchContentKind, string> = {
  guia: 'Guias',
  power: 'Powers',
  ace: 'Aces',
  categoria: 'Categorias',
  pagina: 'Páginas',
};

export function matchesPainelSearchQuery(query: string, ...haystacks: string[]): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return haystacks.some((part) => part.toLowerCase().includes(q));
}

export function filterPainelSearchEntries(
  entries: PainelSearchContentEntry[],
  query: string,
  limit = 40
): PainelSearchContentEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return entries
    .filter(
      (entry) =>
        entry.label.toLowerCase().includes(q) ||
        entry.description.toLowerCase().includes(q) ||
        entry.keywords.toLowerCase().includes(q)
    )
    .slice(0, limit);
}
